import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Chart from "react-apexcharts";
import axios from "axios";
import api from "../../api/api";

import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useLanguage } from "../../context/LanguageContext";
import { useApp } from "../../context/AppContext";
import { useTotalBalance } from "../../hooks/useTotalBalance";
import DonutChart from "../charts/DonutChart";
import TransactionCard from "../Transaction/TransactionCard";
import "../../assets/style/Overview.css";

// Иконки
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { BtcIcon, EthIcon } from "../../assets/img/icons/CryptoIcons";
import verifyImg from "../../assets/img/icons/verify-img.png";
import noverifImg from "../../assets/img/icons/no-verif.png";
import pendingverifImg from "../../assets/img/icons/pending-verif.png";

// Заглушки для графиков*
import MiniLineChart from "../charts/MiniLineChart";

// Карточка для BTC и ETH
function CryptoStatCard({ coin, chartColor }) {
  const [price, setPrice] = useState("0");
  const [series, setSeries] = useState([{ data: [] }]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const symbol = `${coin}USDT`;
  const binanceApiUrl = "https://api.binance.com/api/v3";

  // Опции для графика остаются без изменений
  const chartOptions = {
    chart: {
      type: "area",
      toolbar: { show: false },
      sparkline: { enabled: true },
    },
    colors: [chartColor],
    stroke: { curve: "smooth", width: 2 },
    fill: { type: "gradient", gradient: { opacityFrom: 0.5, opacityTo: 0 } },
    tooltip: {
      enabled: false,
    },
    dataLabels: { enabled: false },
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const klinesResponse = await axios.get(`${binanceApiUrl}/klines`, {
          params: { symbol, interval: "1h", limit: 24 },
        });

        const formattedChartData = klinesResponse.data.map((kline) => ({
          x: kline[0], // Timestamp
          y: parseFloat(kline[4]), // Цена закрытия
        }));

        setSeries([{ name: "Price", data: formattedChartData }]);

        if (formattedChartData.length > 0) {
          setPrice(formattedChartData[formattedChartData.length - 1].y);
        }
      } catch (err) {
        setError("Data loading failed");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    const setupPriceUpdates = () => {
      const intervalId = setInterval(async () => {
        try {
          const priceResponse = await axios.get(
            `${binanceApiUrl}/ticker/price`,
            {
              params: { symbol },
            }
          );
          setPrice(parseFloat(priceResponse.data.price));
        } catch (err) {
          console.error("Ошибка обновления цены:", err);
        }
      }, 5000);
      return intervalId;
    };

    fetchInitialData();
    const priceInterval = setupPriceUpdates();

    return () => clearInterval(priceInterval);
  }, [symbol]);

  if (isLoading) {
    return (
      <Paper
        className=" crypto-stat-card"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper className="card crypto-stat-card">
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} className="card crypto-stat-card">
      <Typography variant="h6" className="crypto-value">
        {coin}
      </Typography>
      <Typography variant="body2" className="crypto-usd-value">
        $
        {parseFloat(price).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </Typography>

      <Box className="crypto-chart-container">
        <Chart
          options={chartOptions}
          series={series}
          type="area"
          height="100%"
        />
      </Box>

      <Box className="crypto-card-footer">
        {coin === "BTC" ? <BtcIcon /> : <EthIcon />}
        <Typography variant="body2">{coin}/USD</Typography>
      </Box>
    </Paper>
  );
}

function CurrencyBalanceItem({ color, name, amount }) {
  return (
    <div className="currency-balance-item">
      <div className="currency-info">
        {/* Динамический цвет здесь задается через style, так как он приходит из props */}
        <div
          className="currency-color-dot"
          style={{ backgroundColor: color }}
        />
        <Typography variant="body1">{name}</Typography>
      </div>
      <Typography variant="body1" className="currency-amount">
        {amount}
      </Typography>
    </div>
  );
}

export default function Overview({ setActiveScreen }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { wallets, fetchWallets, user, fetchUser } = useApp();
  const {
    totalBalanceUSD,
    currentBalanceUSD,
    currencyBalances,
    loading: balanceLoading,
  } = useTotalBalance(wallets);
  const [userLoading, setUserLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);

  // Загружаем кошельки и данные пользователя при монтировании компонента
  useEffect(() => {
    const loadData = async () => {
      try {
        setUserLoading(true);
        setTransactionsLoading(true);
        await fetchWallets();
        await fetchUser();

        // Загружаем транзакции
        try {
          const response = await api.get("/transactions", {
            params: { limit: 4 },
          });
          const list = Array.isArray(response.data)
            ? response.data
            : response.data?.transactions || [];
          setTransactions(list);
        } catch (error) {
          console.error("Error loading transactions:", error);
          setTransactions([]);
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setUserLoading(false);
        setTransactionsLoading(false);
      }
    };
    loadData();
  }, []);

  // Отладочная информация
 /* console.log("=== OVERVIEW DEBUG ===");
  console.log("Overview - wallets:", wallets);
  console.log("Overview - totalBalanceUSD:", totalBalanceUSD);
  console.log("Overview - currencyBalances:", currencyBalances);
  console.log("Overview - balanceLoading:", balanceLoading);
  console.log("Overview - user:", user);
  console.log("Overview - user.is_verif:", user?.is_verif);
  console.log("Overview - is_verif === 1:", user?.is_verif === 1);
  console.log("Overview - userLoading:", userLoading);*/

  // Проверяем каждый кошелек отдельно
  if (wallets && wallets.length > 0) {
    wallets.forEach((wallet, index) => {
      console.log(`Wallet ${index}:`, {
        asset_name: wallet.asset_name,
        balance: wallet.balance,
        balance_type: typeof wallet.balance,
      });
    });
  }

  const handleSendFunds = () => {
    navigate("/dashboard/wallets");
  };

  const handleReceiveFunds = () => {
    navigate("/dashboard/wallets");
  };

  const handleSeeAllTransactions = () => {
    navigate("/dashboard/transactions");
  };

  const userVerifStatus = (user) => {
    if (user && user.is_verif === 1) {
      return <img src={verifyImg} alt="" />;
    } else if (
      user?.doc != null &&
      user?.selfie != null &&
      user?.verif_step === "1"
    ) {
      return <img src={pendingverifImg} alt="" />;
    } else {
      return <img src={noverifImg} alt="" />;
    }
  };

  return (
    <div className="overview-container">
      {/* Верхний ряд: BTC, ETH, KYC */}
      <div className="overview-row">
        <CryptoStatCard
          coin="BTC"
          value="0.00000000"
          usdValue="0.00"
          chartColor="#4e4e8b"
        />
        <CryptoStatCard
          coin="ETH"
          value="0.00000000"
          usdValue="0.00"
          chartColor="#00b0b9"
        />
        <Paper elevation={0} className="card kyc-card">
          {userLoading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <CircularProgress size={40} sx={{ color: "#6366f1" }} />
              <Typography
                variant="body2"
                sx={{ mt: 2, color: "rgba(255, 255, 255, 0.7)" }}
              >
                {t("loading")}
              </Typography>
            </div>
          ) : (
            <>
              <div className="verify-img">{userVerifStatus(user)}</div>
              <Typography
                variant="h6"
                className="kyc-title"
                sx={{
                  color: user && user.is_verif === 1 ? "#22c55e" : "inherit",
                }}
              >
                {user && user.is_verif === 1 ? t("kycIsVerified") : t("kycNotVerified")}
              </Typography>
              <Typography variant="caption" className="kyc-description">
                {user && user.is_verif === 1
                  ? t("kycVerifiedDescription")
                  : t("kycDescription")}
              </Typography>              
              {!((user?.doc != null && user?.selfie != null && user?.verif_step === "1") || (user?.is_verif === 1)) && (
                <Button
                  variant="contained"
                  className="kyc-button"
                  onClick={() => setActiveScreen("Account Settings")}
                >
                  {t("verifyNow")}
                </Button>
              )}
            </>
          )}
        </Paper>
      </div>

      {/* Средний ряд: Баланс */}
      <Paper elevation={0} className="card balance-card">
        <Typography variant="h6" className="card-title">
          {t("currentBalance")}
        </Typography>
        <Typography variant="body2" className="card-subtitle">
          {t("balanceAcrossAccounts")}
        </Typography>
        <div className="balance-card-content">
          <div className="balance-chart-section">
            <DonutChart
              availableBalance={currentBalanceUSD}
              totalBalance={totalBalanceUSD}
              currencyBalances={{
                bitcoin: currencyBalances.bitcoin?.current ?? 0,
                ethereum: currencyBalances.ethereum?.current ?? 0,
                usd: currencyBalances.usd?.current ?? 0,
              }}
              loading={balanceLoading}
            />
          </div>
          <div className="balance-legend-section">
            <div className="balance-summary-grid">
              <div className="balance-summary-box">
                <Typography className="balance-summary-label">
                  💰 Total
                </Typography>
                <Typography className="balance-summary-value">
                  {balanceLoading
                    ? t("loading")
                    : `${totalBalanceUSD.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })} $`}
                </Typography>
              </div>

              <div className="balance-summary-box available">
                <Typography className="balance-summary-label">
                  ⚡ Available
                </Typography>
                <Typography className="balance-summary-value">
                  {balanceLoading
                    ? t("loading")
                    : `${currentBalanceUSD.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })} $`}
                </Typography>
              </div>
            </div>

            <Typography variant="caption" className="card-subtitle">
              {t("availableCurrency")}
            </Typography>

            <div className="currency-list">
              <CurrencyBalanceItem
                color="#ff9800"
                name={t("bitcoin")}
                amount={
                  balanceLoading
                    ? t("loading")
                    : `${(currencyBalances.bitcoin?.current ?? 0).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })} $`
                }
              />
              <CurrencyBalanceItem
                color="#884dff"
                name={t("ethereum")}
                amount={
                  balanceLoading
                    ? t("loading")
                    : `${(currencyBalances.ethereum?.current ?? 0).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })} $`
                }
              />
              <CurrencyBalanceItem
                color="#4caf50"
                name={t("usDollars")}
                amount={
                  balanceLoading
                    ? t("loading")
                    : `${(currencyBalances.usd?.current ?? 0).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })} $`
                }
              />
            </div>
          </div>
        </div>
        <Divider className="card-divider" />
        <div className="balance-actions">
          <Button
            variant="outlined"
            className="fund-button"
            startIcon={<ArrowUpwardIcon />}
            onClick={handleSendFunds}
          >
            {t("sendFunds")}
          </Button>
          <Button
            variant="outlined"
            className="fund-button"
            startIcon={<ArrowDownwardIcon />}
            onClick={handleReceiveFunds}
          >
            {t("receiveFunds")}
          </Button>
        </div>
      </Paper>

      {/* Нижний ряд: Транзакции */}
      <Paper elevation={0} className="card transactions-card">
        <Typography variant="h6" className="card-title">
          {t("transactions")}
        </Typography>
        {transactionsLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <CircularProgress size={40} sx={{ color: "#6366f1" }} />
            <Typography
              variant="body2"
              sx={{ mt: 2, color: "rgba(255, 255, 255, 0.7)" }}
            >
              {t("loading")}
            </Typography>
          </div>
        ) : transactions.length > 0 ? (
          <div className="transactions-list">
            {transactions.map((transaction) => (
              <TransactionCard key={transaction.id} transaction={transaction} />
            ))}
          </div>
        ) : (
          <div className="transactions-placeholder">
            <InboxOutlinedIcon className="placeholder-icon" />
            <Typography variant="h6" className="placeholder-title">
              {t("nothingHere")}
            </Typography>
            <Typography variant="body2" className="card-subtitle">
              {t("noTransactions")}
            </Typography>
          </div>
        )}
        <Typography
          className="see-all-link"
          variant="body2"
          onClick={handleSeeAllTransactions}
        >
          {t("seeAll")} →
        </Typography>
      </Paper>
    </div>
  );
}
